<template>
  <Dialog :open="open" @update:open="handleUpdateOpen">
    <DialogContent class="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col rounded-2xl">
      <DialogHeader>
        <DialogTitle>Filter Vehicles</DialogTitle>
        <DialogDescription>Refine your search to find the perfect vehicle.</DialogDescription>
      </DialogHeader>
      <div class="py-4 overflow-y-auto flex-1">
        <div class="space-y-6 px-1">
          <!-- Conditions -->
          <div>
            <h3 class="text-lg font-medium mb-2">Conditions ({{ filteredConditions.length }})</h3>
            <div class="space-y-2">
              <template v-for="option in filteredConditions" :key="option.value">
                <div
                  v-if="option.count && option.count > 0"
                  class="flex items-center"
                  @click="handleToggleFilterOption('condition', option.value)"
                >
                  <Checkbox
                    :id="`mobile-condition-${option.value}`"
                    :model-value="isFilterOptionSelectedFunc('condition', option.value)"
                    @update:model-value="handleToggleFilterOption('condition', option.value)"
                    class="mr-2"
                  />
                  <label
                    :for="`mobile-condition-${option.value}`"
                    class="flex flex-1 cursor-pointer items-center justify-between"
                  >
                    <span>{{ option.displayValue }}</span>
                    <span class="text-muted-foreground text-sm">({{ option.count ?? 0 }})</span>
                  </label>
                </div>
              </template>
            </div>
          </div>

          <!-- Body Types -->
          <div>
            <h3 class="text-lg font-medium mb-2">Body Types ({{ filteredBodyTypes.length }})</h3>
            <div class="space-y-2">
              <template v-for="option in filteredBodyTypes" :key="option.value">
                <div
                  v-if="option.count && option.count > 0"
                  class="flex items-center"
                  @click="handleToggleFilterOption('body', option.value)"
                >
                  <Checkbox
                    :id="`mobile-body-${option.value}`"
                    :model-value="isFilterOptionSelectedFunc('body', option.value)"
                    @update:model-value="handleToggleFilterOption('body', option.value)"
                    class="mr-2"
                  />
                  <label
                    :for="`mobile-body-${option.value}`"
                    class="flex flex-1 cursor-pointer items-center justify-between"
                  >
                    <span>{{ option.displayValue }}</span>
                    <div class="flex items-center">
                      <span 
                        v-for="(condCount, condName) in option.conditionCounts"
                        :key="condName"
                        v-show="condCount > 0"
                        class="text-xs px-1.5 py-0.5 rounded-full ml-1 capitalize"
                        :class="{
                          'bg-blue-100 text-blue-700': condName === 'new',
                          'bg-green-100 text-green-700': condName === 'used',
                          'bg-yellow-100 text-yellow-700': condName === 'demo',
                          'bg-gray-100 text-gray-700': !['new', 'used', 'demo'].includes(String(condName))
                        }"
                      >
                        {{ condName }}: {{ condCount }}
                      </span>
                      <span class="text-muted-foreground text-sm ml-2">({{ option.count ?? 0 }})</span>
                    </div>
                  </label>
                </div>
              </template>
            </div>
          </div>

          <!-- Makes -->
          <div>
            <h3 class="text-lg font-medium mb-2">Makes ({{ filteredMakes.length }})</h3>
            <div class="space-y-2">
              <template v-for="option in filteredMakes" :key="option.value">
                <div
                  v-if="option.count && option.count > 0"
                  class="flex items-center"
                  @click="handleToggleFilterOption('make', option.value)"
                >
                  <Checkbox
                    :id="`mobile-make-${option.value}`"
                    :model-value="isFilterOptionSelectedFunc('make', option.value)"
                    @update:model-value="handleToggleFilterOption('make', option.value)"
                    class="mr-2"
                  />
                  <label
                    :for="`mobile-make-${option.value}`"
                    class="flex flex-1 cursor-pointer items-center justify-between"
                  >
                    <span>{{ option.displayValue }}</span>
                     <div class="flex items-center">
                      <span 
                        v-for="(condCount, condName) in option.conditionCounts"
                        :key="condName"
                        v-show="condCount > 0"
                        class="text-xs px-1.5 py-0.5 rounded-full ml-1 capitalize"
                        :class="{
                          'bg-blue-100 text-blue-700': condName === 'new',
                          'bg-green-100 text-green-700': condName === 'used',
                          'bg-yellow-100 text-yellow-700': condName === 'demo',
                          'bg-gray-100 text-gray-700': !['new', 'used', 'demo'].includes(String(condName))
                        }"
                      >
                        {{ condName }}: {{ condCount }}
                      </span>
                      <span class="text-muted-foreground text-sm ml-2">({{ option.count ?? 0 }})</span>
                    </div>
                  </label>
                </div>
              </template>
            </div>
          </div>

          <!-- Models -->
          <div>
            <h3 class="text-lg font-medium mb-2">Models ({{ filteredModels.length }})</h3>

            <!-- Search input for models in mobile view -->
            <div class="mb-4 relative">
              <input
                type="text"
                :value="modelSearchQuery"
                @input="handleUpdateModelSearchQuery(($event.target as HTMLInputElement)?.value ?? '')"
                placeholder="Search models..."
                class="w-full px-3 py-1 pr-12 text-sm rounded-md border border-input bg-background text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-primary"
              />
              <div class="absolute inset-y-0 right-0 flex items-center">
                <button
                  v-if="modelSearchQuery"
                  @click="handleClearModelSearch"
                  class="px-2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear model search"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  </svg>
                </button>
                <button
                  class="px-2 text-muted-foreground hover:text-foreground"
                  aria-label="Search models"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>

            <div class="space-y-2">
              <template v-for="option in filteredModels" :key="option.value">
                <div
                  v-if="option.count && option.count > 0"
                  class="flex items-center"
                  @click="handleToggleFilterOption('model', option.value)"
                >
                  <Checkbox
                    :id="`mobile-model-${option.value}`"
                    :model-value="isFilterOptionSelectedFunc('model', option.value)"
                    @update:model-value="handleToggleFilterOption('model', option.value)"
                    class="mr-2"
                  />
                  <label
                    :for="`mobile-model-${option.value}`"
                    class="flex flex-1 cursor-pointer items-center justify-between"
                  >
                    <span>{{ option.displayMake }} {{ option.displayValue }}</span>
                    <div class="flex items-center">
                      <span 
                        v-for="(condCount, condName) in option.conditionCounts"
                        :key="condName"
                        v-show="condCount > 0"
                        class="text-xs px-1.5 py-0.5 rounded-full ml-1 capitalize"
                        :class="{
                          'bg-blue-100 text-blue-700': condName === 'new',
                          'bg-green-100 text-green-700': condName === 'used',
                          'bg-yellow-100 text-yellow-700': condName === 'demo',
                          'bg-gray-100 text-gray-700': !['new', 'used', 'demo'].includes(String(condName))
                        }"
                      >
                        {{ condName }}: {{ condCount }}
                      </span>
                      <span class="text-muted-foreground text-sm ml-2">({{ option.count ?? 0 }})</span>
                    </div>
                  </label>
                </div>
              </template>
            </div>
          </div>

         

        </div>
      </div>

      <!-- Apply Filters Button - Fixed at bottom -->
      <div class="pt-4 border-t border-border mt-auto">


         <!-- Weekly Budget -->
         <div>
            <h3 class="text-lg font-medium mb-2">Weekly Budget</h3>
            <div class="flex justify-between items-center mb-2">
              <span class="text-sm">${{ weeklyBudget[0] }}</span>
              <span class="text-sm">${{ weeklyBudget[1] }}</span>
            </div>
            <ClientOnly>
              <template #default>
                <Slider
                  :min="minWeeklyBudget"
                  :max="maxWeeklyBudget"
                  :model-value="weeklyBudget"
                  :step="1"
                  @update:model-value="handleUpdateBudgetFilter"
                  class="mb-2"
                />
              </template>
              <template #fallback>
                <div class="h-2 bg-muted rounded-full mb-2"></div>
              </template>
            </ClientOnly>
          </div>

          
        <Button
          @click="handleClearAllFilters"
          variant="outline"
          class="w-full my-2 mt-6 hidden"
        >
          Clear All Filters
        </Button>

        <NuxtLink
          :to="{ path: '/cars-for-sale', query: currentFilterQuery }"
          class="bg-primary rounded-lg overflow-hidden flex items-center justify-center text-primary-foreground font-bold p-4 mt-6"
          @click="handleUpdateOpen(false)"
        >
          <span class="mr-2">»</span> Show {{ filteredVehicleCount }} cars
        </NuxtLink>

      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '~/layers/base/components/ui/dialog';
import { Button } from '~/layers/base/components/ui/button';
import { Checkbox } from '~/layers/base/components/ui/checkbox';
import { Slider } from '~/layers/base/components/ui/slider';
import type { FilterOption as OriginalFilterOption } from '../../../types';

// Extend FilterOption locally if needed, or ensure ~/types is updated
interface FilterOption extends OriginalFilterOption {
  conditionCounts?: { [conditionValue: string]: number };
}

interface Props {
  open: boolean;
  filteredConditions: FilterOption[]; // These are simpler, no sub-condition counts needed for the "Conditions" section itself
  filteredBodyTypes: FilterOption[];
  filteredMakes: FilterOption[];
  filteredModels: FilterOption[]; 
  modelSearchQuery: string;
  weeklyBudget: number[];
  minWeeklyBudget: number;
  maxWeeklyBudget: number;
  filteredVehicleCount: number;
  currentFilterQuery: Record<string, any>;
  isFilterOptionSelectedFunc: (filterName: keyof FrontPageArrayFilterKeyMap, optionValue: string) => boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'update:modelSearchQuery', value: string): void;
  (e: 'toggleFilterOption', payload: { filterName: keyof FrontPageArrayFilterKeyMap; optionValue: string }): void;
  (e: 'clearModelSearch'): void;
  (e: 'updateBudgetFilter', value: number[]): void;
  (e: 'clearAllFilters'): void;
}>();

// Define a map for stricter type checking on filterName if desired, or use string directly
interface FrontPageArrayFilterKeyMap {
  condition: any;
  body: any;
  make: any;
  model: any;
}

const handleUpdateOpen = (value: boolean) => emit('update:open', value);
const handleUpdateModelSearchQuery = (value: string) => emit('update:modelSearchQuery', value);
const handleToggleFilterOption = (filterName: keyof FrontPageArrayFilterKeyMap, optionValue: string) => {
  emit('toggleFilterOption', { filterName, optionValue });
};
const handleClearModelSearch = () => emit('clearModelSearch');
const handleUpdateBudgetFilter = (value: number[] | undefined) => {
  if (value) emit('updateBudgetFilter', value);
};
const handleClearAllFilters = () => emit('clearAllFilters');

// No internal state needed here if all data and actions are passed via props/emits
// modelSearchQuery is v-model like via :value and @input
// weeklyBudget is handled by Slider's :model-value and @update:model-value

// Helper functions like formatPrice, if only used in template and simple, can remain
// or be passed if complex/shared. For now, weeklyBudget is just numbers.

// The NuxtLink will close the dialog on click, which is good UX.
</script> 